<template>
    <div v-if="banner.type === DsPromoBannerNewType.Link" class="ds-promo-banner-new" :class="bannerSizeClass">
        <div
            class="ds-promo-banner-new__inner"
            :class="[`ds-promo-banner-new__inner--${theme}`, { 'ds-promo-banner-new--gradient': banner.gradient }]"
        >
            <header v-if="banner.headerType !== DsPromoBannerNewHeaderType.None" class="header-with-icon">
                <template v-if="banner.headerType === DsPromoBannerNewHeaderType.Text">
                    <div class="header-with-icon__icon-wrapper">
                        <DsIcon :name="(banner.headerIcon as SvgIcons)" :width="16" :height="16" />
                    </div>
                    <h3 class="header-with-icon__text" v-html="banner.headerText"></h3>
                </template>
                <template v-else>
                    <div
                        :class="[
                            'header-with-icon__promo-icon-outer',
                            `header-with-icon__promo-icon-outer--${banner.lottery}`,
                        ]"
                    >
                        <WebpImage
                            :src="banner.lotteryHeaderImage"
                            css-class="header-with-icon__promo-icon"
                            :get2x="true"
                            alt=""
                        />
                    </div>
                    <h3
                        class="header-with-icon__text"
                        v-html="
                            banner.headerType === DsPromoBannerNewHeaderType.LotteryIconCustomText
                                ? banner.headerText
                                : banner.lotteryDisplayName
                        "
                    ></h3>
                </template>
            </header>

            <WebpImage :src="banner.backgroundImage" css-class="ds-promo-banner-new__bg" alt="" />

            <WebpWebmMedia
                v-if="banner.image"
                :src="banner.image"
                css-class="ds-promo-banner-new__image"
                :loop-video="true"
                alt=""
            />

            <div class="ds-promo-banner-new__body">
                <div class="ds-promo-banner-new__texts">
                    <h4 class="ds-promo-banner-new__heading" v-html="banner.headline"></h4>
                    <p class="ds-promo-banner-new__description" v-if="banner.text" v-html="banner.text"></p>
                </div>
                <div class="ds-promo-banner-new__buttons">
                    <DsButton
                        v-if="banner.buttonText && banner.linkType !== DsPromoBannerNewLinkType.AppDownload"
                        size="sm"
                        type="primary"
                        :theme="theme"
                    >
                        {{ banner.buttonText }}</DsButton
                    >
                    <template v-else-if="banner.linkType === DsPromoBannerNewLinkType.AppDownload">
                        <a :href="banner.appLinks.iosUrl" class="ios-only">
                            <img
                                src="/frontend/web/img/design-system/store/apple.svg"
                                alt="App store"
                                class="button-store"
                            />
                        </a>
                        <a :href="banner.appLinks.androidUrl" class="android-only">
                            <img
                                src="/frontend/web/img/design-system/store/google-play.svg"
                                alt="Google Play store"
                                class="button-store"
                            /> </a
                    ></template>
                </div>
            </div>
            <a
                v-if="banner.linkType !== DsPromoBannerNewLinkType.AppDownload"
                :href="link"
                :target="banner.openInNewTab ? '_blank' : undefined"
                :rel="banner.openInNewTab ? 'noopener noreferrer' : undefined"
                class="ds-promo-banner-new__link-overlay"
                :class="linkClass"
                @click="onClickBanner"
                :data-game-id="banner.linkGameExternalId"
                :data-app-event="banner.mobileAppEvent"
                :data-app-argument="banner.mobileAppArgument"
            >
            </a>
        </div>
    </div>
    <div v-else class="cp-pop-over__stream-banner">
        <button type="button" class="lotteries-stream-banner" @click="openVideoStream">
            <div class="lotteries-stream-banner__badge">
                <div class="lotteries-stream-banner__badge-icon">
                    <DsIcon name="video" :width="16" :height="16" />
                </div>
                <span class="lotteries-stream-banner__badge-text"> Živé losování </span>
            </div>

            <WebpImage
                src="/frontend/web/img/lotteries/quick-lotteries/stream.jpg"
                css-class="lotteries-stream-banner__img"
                alt=""
            />

            <span class="lotteries-stream-banner__btn"></span>
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { isMobileApp, triggerMobileAppEvent } from "@sazka/mobile-apps/mobile-app-integration";
import { UrlUtils } from "@sazka/utils/url";
import {
    DsPromoBannerNewDto,
    DsPromoBannerNewHeaderType,
    DsPromoBannerNewLinkType,
    DsPromoBannerNewType,
} from "./ds-promo-banner-new";
import DsButton from "../button/ds-button.vue";
import DsIcon from "../icon/ds-icon.vue";
import { SvgIcons } from "../icon/types";
import WebpImage from "../image/webp-image.vue";
import { Theme } from "../types/theme";
import WebpWebmMedia from "../video/webp-video.vue";

const { banner, size = "low" } = defineProps<{
    banner: DsPromoBannerNewDto;
    size?: "high" | "low" | "default";
    analyticsBannerPage?: string;
}>();

const emit = defineEmits(["bannerMount", "bannerClick", "openVideoStream"]);

const bannerSizeClass = computed(() => (size && size != "default" ? `ds-promo-banner-new--${size}` : ""));

const theme = computed(() => {
    return banner.dsTheme.toLowerCase() as Theme;
});

const link = computed(() => {
    if (banner.linkType === DsPromoBannerNewLinkType.Url && banner.urlLink) {
        const url = banner.urlLink?.replace("~/", "/");

        if (!url) return null;

        let bannerPage = "else";
        if (environment.nodeAliasPath === "/loterie") {
            bannerPage = "mainCategoryPopoverBanner";
        } else if (environment.nodeAliasPath === "/moje-sazky/vsazene") {
            bannerPage = "mainMyBetsBanner";
        } else if (environment.nodeAliasPath.endsWith("/kontrola-a-vysledky")) {
            bannerPage = "mainCheckBetBanner";
        }

        return UrlUtils.addQueryParameter(url, `bannerPage=${bannerPage}`);
    }

    return banner.urlLink;
});

const linkClass = computed(() => {
    switch (banner.linkType) {
        case DsPromoBannerNewLinkType.Casino:
            return "game-launcher";
        case DsPromoBannerNewLinkType.Video:
            return "btn-video";
        default:
            return "";
    }
});

function onClickBanner(event: Event) {
    if (banner.mobileAppEvent && isMobileApp) {
        event.preventDefault();
        triggerMobileAppEvent(banner.mobileAppEvent, banner.mobileAppArgument);
    }

    emit("bannerClick");
}

const openVideoStream = () => {
    emit("openVideoStream");
};

onMounted(() => {
    emit("bannerMount");
});
</script>
